import { Inject, Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { AuthService as ExternalAuthService } from './external-auth.service';
import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs';
import { HttpEvent } from '@angular/common/http';
import { Env } from '@usimweb/core/models';

@Injectable({ providedIn: 'root' })
export class AuthHandler {
  private static readonly APPSTORE = 'appstore';
  private static readonly ULTRASIM_WEB = 'ultrasimweb';
  private static ENV: Env = {} as Env;

  public isAuthenticatedListener = new BehaviorSubject<boolean>(false);

  constructor(
    @Inject('environment') private environment: Env,
    private authService: AuthService,
    private externalAuthService: ExternalAuthService
  ) {
    AuthHandler.ENV = this.environment;
  }

  public authListener() {
    if (this.environment.deployment === AuthHandler.APPSTORE) {
      if (this.authService.accessToken === undefined || this.authService.accessToken === 'undefined') {
        const subscription = this.authService.initializationRequest().subscribe(() => {
          this.isAuthenticatedListener.next(true);
          subscription.unsubscribe();
        });
      } else {
        this.authService.setUserName();
        this.isAuthenticatedListener.next(true);
      }
    }

    if (this.environment.deployment === AuthHandler.ULTRASIM_WEB) {
      if (this.externalAuthService.isAuthenticated()) {
        this.isAuthenticatedListener.next(true);
      } else {
        this.externalAuthService.userSet.subscribe(() => {
          this.isAuthenticatedListener.next(true);
        });
      }
    }
  }

  public initializationRequest(): Observable<any> {
    if (this.environment.deployment === AuthHandler.APPSTORE) {
      return this.authService.initializationRequest();
    }
    return EMPTY;
  }

  public authenticated(): Observable<boolean> {
    if (this.environment.deployment === AuthHandler.APPSTORE) {
      if (this.authService.accessToken === undefined || this.authService.accessToken === 'undefined') {
        this.isAuthenticatedListener.subscribe((authenticated: boolean) => {
          if (authenticated) {
            return of(true);
          }
          return of(false);
        });
      } else {
        return of(true);
      }
    }
    if (this.environment.deployment === AuthHandler.ULTRASIM_WEB) {
      return of(true);
    }
    return of(false);
  }

  // FIX ME remove return
  public logout() {
    if (this.environment.deployment === AuthHandler.APPSTORE) {
      return this.authService.logout();
    }
    if (this.environment.deployment === AuthHandler.ULTRASIM_WEB) {
      return this.externalAuthService.logout();
    }
    return null;
  }

  public forwardToLogin() {
    this.authService.forwardToLogin();
  }

  public get accessToken() {
    if (this.environment.deployment === AuthHandler.APPSTORE) {
      return this.authService.accessToken;
    }
    if (this.environment.deployment === AuthHandler.ULTRASIM_WEB) {
      return this.externalAuthService.accessToken;
    }
    return null;
  }

  public static get basfUserName() {
    if (AuthHandler.ENV.deployment === AuthHandler.APPSTORE) {
      return AuthService.basfUserName;
    }
    if (AuthHandler.ENV.deployment === AuthHandler.ULTRASIM_WEB) {
      return ExternalAuthService.basfUserName;
    }
    return AuthService.basfUserName;
  }

  public static get userName() {
    if (AuthHandler.ENV.deployment === AuthHandler.APPSTORE) {
      return AuthService.userName;
    }
    if (AuthHandler.ENV.deployment === AuthHandler.ULTRASIM_WEB) {
      return ExternalAuthService.userName;
    }
    return null;
  }

  public static get firstName() {
    if (AuthHandler.ENV.deployment === AuthHandler.ULTRASIM_WEB) {
      return ExternalAuthService.firstName;
    }
    return '';
  }

  public static get lastName() {
    if (AuthHandler.ENV.deployment === AuthHandler.ULTRASIM_WEB) {
      return ExternalAuthService.lastName;
    }
    return '';
  }

  public static get mail() {
    if (AuthHandler.ENV.deployment === AuthHandler.ULTRASIM_WEB) {
      return ExternalAuthService.mail;
    }
    return '';
  }

  public get isTokenExpired(): boolean {
    if (this.environment.deployment === AuthHandler.ULTRASIM_WEB) {
      return this.externalAuthService.isTokenExpired();
    }
    return false;
  }

  public get authenticate() {
    if (this.environment.deployment === AuthHandler.ULTRASIM_WEB) {
      return this.externalAuthService.authenticate();
    }
    return null;
  }

  public get refreshToken(): Observable<HttpEvent<any>> {
    if (this.environment.deployment === AuthHandler.APPSTORE) {
      return this.authService.refreshToken();
    }
    return EMPTY;
  }
}
